// PACKAGE #11 — Listing Boost purchase sheet.
// Opened from ListingDetailsActions' "Підняти оголошення" button. Uses the
// existing #10 tariffs only (24h=49 / 3d=129 / 7d=249 Coins) and the
// existing WalletContext balance — no inline Monobank/Apple/Google Pay here
// (that's Package #12).
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { getAccessToken } from "../../awsAuth";
import { useWallet } from "../../contexts/WalletContext";
import "./BoostSheet.scss";

const API_BASE = process.env.REACT_APP_API_BASE || "http://localhost:5000";

const PLANS = [
  { durationHours: 24, label: "24 години", price: 49 },
  { durationHours: 72, label: "3 дні", price: 129 },
  { durationHours: 168, label: "7 днів", price: 249 },
];

function fmtDate(msOrIso) {
  if (!msOrIso) return "";
  try {
    const t = typeof msOrIso === "number" ? msOrIso : new Date(msOrIso).getTime();
    return new Date(t).toLocaleString("uk-UA", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "";
  }
}

const newRequestId = () =>
  typeof crypto !== "undefined" && crypto.randomUUID
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2)}`;

export default function BoostSheet({ open, onClose, listingId, onBoosted }) {
  const { balance, loading: balanceLoading, refresh: refreshWallet } = useWallet();

  const [status, setStatus] = useState(null); // { active, endsAtSec, raisedUntil }
  const [statusLoading, setStatusLoading] = useState(true);
  const [selectedHours, setSelectedHours] = useState(24);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  // PACKAGE #12 Phase C — low-friction Boost auto-topup: on 402 this holds
  // the exact shortfall so we can offer "Поповнити X ₴ і підняти" inline
  // instead of sending the user to Balance and back. `null` = not showing.
  const [insufficient, setInsufficient] = useState(null); // { need } | null
  const [toppingUp, setToppingUp] = useState(false);

  const requestIdRef = React.useRef(null);

  const loadStatus = useCallback(async () => {
    if (!listingId) return;
    setStatusLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/boost/status/${encodeURIComponent(listingId)}`);
      const data = await res.json();
      setStatus(data);
    } catch (e) {
      setStatus(null);
    } finally {
      setStatusLoading(false);
    }
  }, [listingId]);

  useEffect(() => {
    if (!open) return;
    setError("");
    setInsufficient(null);
    setSelectedHours(24);
    requestIdRef.current = null;
    loadStatus();
    refreshWallet();
  }, [open, loadStatus, refreshWallet]);

  const selectedPlan = useMemo(
    () => PLANS.find((p) => p.durationHours === selectedHours) || PLANS[0],
    [selectedHours]
  );

  const handleBuy = async () => {
    if (!listingId || submitting) return;
    setSubmitting(true);
    setError("");
    setInsufficient(null);

    if (!requestIdRef.current) requestIdRef.current = newRequestId();

    try {
      const token = await getAccessToken().catch(() => null);
      const res = await fetch(`${API_BASE}/api/boost/start`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          listingId,
          durationHours: selectedPlan.durationHours,
          requestId: requestIdRef.current,
        }),
      });

      if (res.status === 402) {
        const data = await res.json().catch(() => null);
        const need = Number(data?.need);
        setInsufficient({ need: Number.isFinite(need) ? need : null });
        return;
      }

      if (res.status === 409) {
        setError("Просування вже активне для цього оголошення.");
        await loadStatus();
        return;
      }

      if (!res.ok) {
        const data = await res.json().catch(() => null);
        throw new Error(data?.message || `HTTP ${res.status}`);
      }

      const data = await res.json();

      requestIdRef.current = null;
      await refreshWallet();
      await loadStatus();
      onBoosted?.(data);
    } catch (e) {
      setError(e?.message || "Не вдалося підняти оголошення");
    } finally {
      setSubmitting(false);
    }
  };

  // PACKAGE #12 Phase C — creates a real Monobank invoice for exactly the
  // wallet shortfall and redirects to the hosted checkout. The confirmed
  // top-up automatically resumes THIS exact boost server-side (see
  // paymentFulfillmentService.js's applyWalletTopupFulfillment) — the user
  // never has to come back here and tap "Підняти" again.
  const handleTopupAndBuy = async () => {
    if (!listingId || toppingUp) return;
    setToppingUp(true);
    setError("");

    if (!requestIdRef.current) requestIdRef.current = newRequestId();

    try {
      const token = await getAccessToken().catch(() => null);
      const res = await fetch(`${API_BASE}/api/boost/topup-and-start`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          listingId,
          durationHours: selectedPlan.durationHours,
          requestId: requestIdRef.current,
        }),
      });

      const data = await res.json().catch(() => null);

      if (!res.ok || !data?.redirectUrl) {
        throw new Error(data?.message || "Не вдалося створити рахунок на оплату");
      }

      window.location.href = data.redirectUrl;
    } catch (e) {
      setError(e?.message || "Не вдалося створити платіж");
      setToppingUp(false);
    }
  };

  if (!open || typeof document === "undefined") return null;

  const isActive = !!status?.active;

  return createPortal(
    <div className="boost-sheet__backdrop" onClick={onClose} role="dialog" aria-modal="true">
      <div className="boost-sheet__card" onClick={(e) => e.stopPropagation()}>
        <div className="boost-sheet__title">Підняти оголошення</div>

        {statusLoading ? (
          <div className="boost-sheet__state">Завантаження…</div>
        ) : isActive ? (
          <div className="boost-sheet__active">
            Просування активне до {fmtDate(status.endsAtSec ? status.endsAtSec * 1000 : status.raisedUntil)}
          </div>
        ) : (
          <>
            <div className="boost-sheet__plans">
              {PLANS.map((p) => (
                <button
                  key={p.durationHours}
                  type="button"
                  className={`boost-sheet__plan ${selectedHours === p.durationHours ? "is-selected" : ""}`}
                  onClick={() => setSelectedHours(p.durationHours)}
                  disabled={submitting}
                >
                  <span className="boost-sheet__planLabel">{p.label}</span>
                  <span className="boost-sheet__planPrice">{p.price} Tokens</span>
                </button>
              ))}
            </div>

            <div className="boost-sheet__balance">
              Ваш баланс: <strong>{balanceLoading ? "…" : balance} Tokens</strong>
            </div>

            {insufficient ? (
              <div className="boost-sheet__insufficient">
                <div className="boost-sheet__error">
                  Недостатньо Tokens
                  {Number.isFinite(insufficient.need) ? ` (потрібно ${insufficient.need})` : ""}.
                </div>
                <button
                  type="button"
                  className="boost-sheet__cta"
                  onClick={handleTopupAndBuy}
                  disabled={toppingUp}
                >
                  {toppingUp ? "Створюємо рахунок…" : "Поповнити і підняти"}
                </button>
              </div>
            ) : (
              <>
                {error && <div className="boost-sheet__error">{error}</div>}

                <button
                  type="button"
                  className="boost-sheet__cta"
                  onClick={handleBuy}
                  disabled={submitting}
                >
                  {submitting ? "Підняття…" : `Підняти за ${selectedPlan.price} Tokens`}
                </button>
              </>
            )}
          </>
        )}

        <button className="boost-sheet__close" onClick={onClose}>
          Закрити
        </button>
      </div>
    </div>,
    document.body
  );
}
